import { ReconfigureAgentPanelView } from '../view/ReconfigureAgentPanelView';
import { templateReconfigureAgentPanel } from '../view/template';
import { ReconfigureAgentPanelRepository } from '../repository/ReconfigureAgentPanelRepository';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../../framework/BaseWebComponentController';
import { OnReconfigurePanelClose, ReconfigureAgentPanelEvents } from '../../common/events/ReconfigureAgentPanelEvents';
import {
  OnReconfigureAgentTypeSelect,
  ReconfigureAgentType,
  ReconfigureAgentTypeEvent,
} from '../../common/reconfigureAgentTypeForm/types';
import { PackageGeneralEvent } from '../../../../packages/common/packageGeneralForm/types/PackageGeneralEvent';
import { Feature } from '@nirvana/shared/constants';
import { MonthlyProductType } from '../../../../license/types/MonthlyProductType';
import { ScanModes } from '../../../../packages/common/packageScanModesForm/types/ScanModes';
import {
  OnScanModeRadioSelectionChanged,
  PackageScanModesEvent,
} from '../../../../packages/common/packageScanModesForm/types/PackageScanModesEvent';
import { EndpointModules, PackageData } from '../../../../packages/types/package-data';
import _ from 'lodash';
import { combineLatest, Subscription } from 'rxjs';
import { ReconfigureAgentTaskData } from '../../../types/reconfigure-agent-task-data';
import { ReconfigureAgentPanelWebComponent } from '../component/ReconfigureAgentPanelWebComponent';
import { OnReconfigureAgentModuleIsChecked, ReconfigureAgentPanelEvent, ReconfigureAgentViewData } from '../types';
import moment from 'moment';
import { TaskType } from '../../../../tasks/networkTasks/types';
import { DeepPartial } from 'typeorm';
import { PackageModulesFormWebComponent } from '../../../../packages/common/packageModulesForm';
import { Dependencies } from '../../../types/dependencies';
import { translateFn } from '../../../../utils/translator.helper';
import { SchedulerDataResponse } from '../../common/reconfigureAgentShedulerForm/types';

export class ReconfigureAgentPanelController extends BaseWebComponentController {
  public component: ReconfigureAgentPanelWebComponent;
  public dataRepository: ReconfigureAgentPanelRepository;
  public view: ReconfigureAgentPanelView;
  public viewConstructor: typeof ReconfigureAgentPanelView;
  public dataRepositoryConstructor: typeof ReconfigureAgentPanelRepository;
  public dependencies: Dependencies;

  private validSubscription: Subscription | null;
  private companyId: string;
  private selectedCompaniesIds: string[];
  private availableFeatures: Feature[];
  private currentReconfigureOperation: ReconfigureAgentType;

  constructor(component: ReconfigureAgentPanelWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: ReconfigureAgentPanelView,
      viewTemplate: templateReconfigureAgentPanel,
      dataRepositoryConstructor: ReconfigureAgentPanelRepository,
    });
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$wrapper,
        event: ReconfigureAgentPanelEvents.OnViewInit,
        callback: this.showReconfigureAgentPanel,
      },
      {
        element: this.view.dom.$wrapper,
        event: ReconfigureAgentTypeEvent.OnReconfigureTypeRadioSelectionChanged,
        callback: this.onReconfigureTypeChange,
      },
      {
        element: this.view.dom.$wrapper,
        event: PackageGeneralEvent.OnOperationModeSelect,
        callback: this.onReconfigureOperationModeSelect,
      },
      {
        element: this.view.dom.$wrapper,
        event: ReconfigureAgentPanelEvent.OnReconfigureAgentModulesChanged,
        callback: this.onReconfigureAgentModulesChanged,
      },
      {
        element: this.view.dom.$reconfigureScanModesForm,
        event: PackageScanModesEvent.OnScanModeRadioSelectionChanged,
        callback: this.onScanModeRadioSelectionChange,
      },
      {
        element: this.view.dom.$reconfigureScanModesForm,
        event: PackageScanModesEvent.OnCustomScanModeOptionChanged,
        callback: this.onCustomScanModeChange,
      },
      {
        element: this.view.dom.$wrapper,
        event: PackageScanModesEvent.OnInit,
        callback: this.onCustomScanModeFinishInit,
      },
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.emitCloseReconfigureAgentWindowEvent,
      },
      {
        element: this.view.dom.$saveButton,
        event: 'click',
        callback: this.saveReconfigureAgentTask,
      },
    ];
  }

  private showReconfigureAgentPanel(): void {
    this.setDefaultValidationSubscription();
    const modulesForm: PackageModulesFormWebComponent = this.view.dom.$reconfigureAgentModulesForm;

    modulesForm.controller.view.formGroup.setDisabled(false);
    modulesForm.packageData = {
      productType: this.view.dom.$reconfigureAgentModulesForm.getDefaultOperationMode,
      modules: this.availableFeatures as EndpointModules,
      removeCompetitors: true,
    };
  }

  private setDefaultValidationSubscription(): void {
    this.validSubscription?.unsubscribe();
    this.validSubscription = combineLatest([
      this.view.dom.$reconfigureAgentSchedulerForm.valid$,
      this.view.dom.$reconfigureSecurityServerAssignmentGrid.valid$,
    ]).subscribe({
      next: ([reconfigureAgentSchedulerFormValid, reconfigureSecurityServerAssignmentValid]) => {
        this.view.dom.$saveButton.disabled = !(
          reconfigureAgentSchedulerFormValid && reconfigureSecurityServerAssignmentValid
        );
      },
    });
  }

  private async saveReconfigureAgentTask(): Promise<void> {
    this.view.dom.$saveButton.showSpinner = true;
    this.view.dom.$saveButton.disabled = true;
    let responseState = false;

    try {
      await this.createReconfigureAgentTask();
      responseState = true;
    } catch (e: any) {} // error logging of the Exception is handled in the ExtJs code for now

    this.view.dom.$saveButton.showSpinner = false;
    this.view.dom.$saveButton.disabled = false;

    this.emitCloseReconfigureAgentWindowEvent();
  }

  private async createReconfigureAgentTask(): Promise<void> {
    //predefined components have different formGroup format
    const modulesData: DeepPartial<PackageData> = this.view.dom.$reconfigureAgentModulesForm.value;
    const removeCompetitors: boolean = modulesData?.settings?.removeCompetitors ?? false;
    delete modulesData?.settings?.removeCompetitors;
    const reconfigureAgentTaskComponentsSettings = _.merge(
      this.view.dom.$reconfigureScanModesForm.value,
      this.view.dom.$reconfigureSecurityServerAssignmentGrid.value,
      modulesData
    );

    const schedulerData: SchedulerDataResponse = this.view.dom.$reconfigureAgentSchedulerForm.value;
    const reconfigureAgentTaskSettings: ReconfigureAgentTaskData = _.merge(
      schedulerData,
      {
        settings: {
          specifics: reconfigureAgentTaskComponentsSettings['settings'],
        },
      },
      {
        settings: {
          type: TaskType.TASK_TYPE_MODIFY_INSTALLER_ENDPOINT_PARENT,
          specifics: {
            modulesOperationType: this.view.dom.$reconfigureAgentTypeForm.selectedOperation,
          },
          taskName: this.getTaskName(schedulerData?.settings?.specifics?.scheduler?.scheduledDeploy),
        },
      }
    ) as ReconfigureAgentTaskData;

    reconfigureAgentTaskSettings.settings.specifics.removeCompetitorsIfNeeded = removeCompetitors;

    await this.dependencies.makeRequest(reconfigureAgentTaskSettings);
  }

  private getTaskName(scheduledDeploy: boolean | undefined): string {
    if (!scheduledDeploy) {
      return translateFn('Reconfigure agent settings');
    }

    return translateFn(`Scheduled reconfigure agent settings ${moment(new Date()).format('YYYY-MM-DD')}`);
  }

  async getDataForView(): Promise<ReconfigureAgentViewData> {
    this.selectedCompaniesIds = this.dependencies.getSelectedCompanies();
    this.companyId = _.first(this.selectedCompaniesIds) ?? '';
    this.availableFeatures = await this.dataRepository.getFeaturesByCompanyIds({
      companyIds: this.selectedCompaniesIds,
    });
    return {
      companyId: this.companyId,
      availableFeatures: this.availableFeatures,
    };
  }

  private onReconfigureTypeChange(event: OnReconfigureAgentTypeSelect): void {
    const reconfigureOperation = event.detail.reconfigureAgentType;
    const operationMode = this.view.dom.$reconfigureAgentModulesForm.operationMode;
    const isEdrSelected = operationMode === MonthlyProductType.BitdefenderEdr;
    const isReconfigureTypeRemove = reconfigureOperation === ReconfigureAgentType.Remove;
    const isAntimalwareChecked = reconfigureOperation !== ReconfigureAgentType.Remove;

    if (this.currentReconfigureOperation && this.currentReconfigureOperation === reconfigureOperation) {
      return;
    }

    this.view.dom.$reconfigureAgentModulesForm.showAllModulesToggle(!isEdrSelected && isReconfigureTypeRemove);
    this.currentReconfigureOperation = reconfigureOperation;
    this.toggleVisibilityScanModesSection();

    this.view.setModulesRequirementsText(reconfigureOperation);

    if (isEdrSelected) {
      this.setModules();
      return;
    }

    // for Detection and prevention change only antimalware modules
    this.view.dom.$reconfigureAgentModulesForm.controller.view.modulesFields.antimalware.$uiCheckbox.Input.setFormValue(
      isAntimalwareChecked
    );
    this.view.dom.$reconfigureAgentModulesForm.controller.view?.formGroup?.validate(true);
  }

  private onReconfigureAgentModulesChanged(event: OnReconfigureAgentModuleIsChecked): void {
    this.view.dom.$saveButton.disabled = event.detail.allModulesUnchecked;
  }

  private onReconfigureOperationModeSelect(): void {
    this.setModules();
    this.toggleVisibilityScanModesSection();
  }

  private toggleVisibilityScanModesSection(): void {
    const selectedReconfigureOperation = this.view.dom.$reconfigureAgentTypeForm.selectedOperation;
    if (selectedReconfigureOperation === ReconfigureAgentType.Match && this.selectedCompaniesIds.length === 1) {
      const operationMode = this.view.dom.$reconfigureAgentModulesForm.operationMode;
      this.view.dom.$reconfigureScanModesForm.toggleVisibilityBasedOnProductType(operationMode);
    } else {
      this.view.dom.$reconfigureScanModesForm.toggleVisibilityBasedOnProductType(MonthlyProductType.BitdefenderEdr);
    }
  }

  private setModules(): void {
    if (!this.view.dom.$reconfigureAgentModulesForm.controller.view.formGroup) {
      return;
    }

    const selectedReconfigureOperation: ReconfigureAgentType =
      this.view.dom.$reconfigureAgentTypeForm.selectedOperation;
    const selectedOperationMode: MonthlyProductType = this.view.dom.$reconfigureAgentModulesForm.operationMode;

    const isReconfigureTypeRemove: boolean = selectedReconfigureOperation === ReconfigureAgentType.Remove;
    const isEdrSelected: boolean = selectedOperationMode === MonthlyProductType.BitdefenderEdr;
    this.view.dom.$reconfigureAgentModulesForm.showAllModulesToggle(!isEdrSelected && isReconfigureTypeRemove);

    this.view.dom.$reconfigureAgentModulesForm.packageData = {
      productType: selectedOperationMode,
      modules: this.getModules(selectedReconfigureOperation, selectedOperationMode),
      removeCompetitors: true,
    };

    this.view.dom.$reconfigureAgentModulesForm.controller.setOperationMode();
  }

  private async onScanModeRadioSelectionChange(ev: OnScanModeRadioSelectionChanged): Promise<void> {
    const scanType: ScanModes = ev.detail.selectedValue;
    const hasCustomModeOptions = this.view.dom.$reconfigureScanModesForm.hasCustomModeOptions(scanType);

    await this.view.dom.$reconfigureSecurityServerAssignmentGrid.showSecurityServerAssignmentSection(
      hasCustomModeOptions,
      this.companyId
    );
  }

  private async onCustomScanModeChange(ev: WebComponentEvent): Promise<void> {
    this.view.dom.$reconfigureScanModesForm.useDefaultValues = false;
    await this.view.dom.$reconfigureSecurityServerAssignmentGrid.showSecurityServerAssignmentSection(
      ev.detail.hasCentralScan,
      this.companyId
    );
  }

  private async onCustomScanModeFinishInit(ev: WebComponentEvent): Promise<void> {
    const isVisible = this.view.dom.$reconfigureScanModesForm.hasCentralScan(ev.detail);
    await this.view.dom.$reconfigureSecurityServerAssignmentGrid.showSecurityServerAssignmentSection(
      isVisible,
      this.companyId
    );
  }

  private emitCloseReconfigureAgentWindowEvent(): void {
    this.component.dispatchEvent(new OnReconfigurePanelClose());
  }

  private getModules(reconfigureOperation, operationMode): EndpointModules {
    const enabledForEDRAndMatchList =
      reconfigureOperation === ReconfigureAgentType.Match && operationMode === MonthlyProductType.BitdefenderEdr;
    return {
      antimalware:
        _.includes([ReconfigureAgentType.Add, ReconfigureAgentType.Match], reconfigureOperation) &&
        operationMode !== MonthlyProductType.BitdefenderEdr,
      firewall: false,
      userControl: enabledForEDRAndMatchList,
      networkMonitor: enabledForEDRAndMatchList,
      antiphishing: enabledForEDRAndMatchList,
      trafficScan: enabledForEDRAndMatchList,
      updateServer: false,
      avc: enabledForEDRAndMatchList,
      mailServers: false,
      deviceControl: false,
      powerUser: false,
      applicationControl: false,
      encryption: false,
      edrSensor: enabledForEDRAndMatchList,
      patchManagement: false,
      patchManagementServer: false,
      containerProtection: false,
      exploitMitigation: false,
      integrityMonitor: false,
      frat: false,
    };
  }
}
